import React, { useEffect, useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Chip,
  Paper,
} from '@mui/material';
import { subscriptionApi } from './subscriptionApi';
import { MENU_LIST } from './menuList';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const currency = company?.currency ?? '₹';

const emptyForm = {
  planId: 0,
  planName: '',
  description: '',
  price: '',
  durationDays: '',
  maxUsers: '',
  maxRooms: '',
  isTrialPlan: false,
  isActive: true,
  propertType: 'H',
  menuKeys: [],
};

export default function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const loadPlans = async () => {
    try {
      const res = await subscriptionApi.getPlans(false);
      setPlans(res.data || []);
    } catch (err) {
      console.error('Load plans failed', err);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const editPlan = (p) => {
    setForm({
      planId: p.planId,
      planName: p.planName ?? '',
      description: p.description ?? '',
      price: p.price ?? '',
      durationDays: p.durationDays ?? '',
      maxUsers: p.maxUsers ?? '',
      maxRooms: p.maxRooms ?? '',
      isTrialPlan: p.isTrialPlan ?? false,
      isActive: p.isActive ?? true,
      propertType: p.propertType ?? 'H',
      menuKeys: p.menuKeys ?? [],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => setForm(emptyForm);

  // Toggle a single menu in the plan's menu set.
  const toggleMenu = (key) =>
    setForm((f) => ({
      ...f,
      menuKeys: f.menuKeys.includes(key)
        ? f.menuKeys.filter((k) => k !== key)
        : [...f.menuKeys, key],
    }));
  const selectAllMenus = () =>
    setForm((f) => ({ ...f, menuKeys: MENU_LIST.map((m) => m.key) }));
  const clearMenus = () => setForm((f) => ({ ...f, menuKeys: [] }));

  const savePlan = async () => {
    if (!form.planName || !form.price || !form.durationDays) {
      alert('Plan name, price and duration are required.');
      return;
    }
    setLoading(true);
    try {
      await subscriptionApi.savePlan({
        planId: form.planId,
        planName: form.planName,
        description: form.description,
        price: parseFloat(form.price) || 0,
        durationDays: parseInt(form.durationDays, 10) || 0,
        maxUsers: form.maxUsers === '' ? null : parseInt(form.maxUsers, 10),
        maxRooms: form.maxRooms === '' ? null : parseInt(form.maxRooms, 10),
        isTrialPlan: form.isTrialPlan,
        isActive: form.isActive,
        propertType: form.propertType,
        menuKeys: form.menuKeys,
      });
      alert(form.planId > 0 ? 'Plan updated.' : 'Plan created.');
      resetForm();
      loadPlans();
    } catch (err) {
      console.error('Save plan failed', err);
      alert(err.response?.data?.message || 'Failed to save plan.');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await subscriptionApi.togglePlan(p.planId, !p.isActive);
      loadPlans();
    } catch (err) {
      console.error('Toggle failed', err);
      alert('Failed to update plan status.');
    }
  };

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 6 }}>
      <Typography variant='h5' sx={{ fontWeight: 700, mb: 3 }}>
        Subscription Plans
      </Typography>

      {/* ── Add / Edit form ── */}
      <Card sx={{ borderRadius: 3, mb: 4, boxShadow: 3 }}>
        <CardContent>
          <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
            {form.planId > 0 ? 'Edit Plan' : 'Add New Plan'}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Plan Name'
                fullWidth
                value={form.planName}
                onChange={handleChange('planName')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={`Price (${currency})`}
                type='number'
                fullWidth
                value={form.price}
                onChange={handleChange('price')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Duration (Days)'
                type='number'
                fullWidth
                value={form.durationDays}
                onChange={handleChange('durationDays')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Max Users (optional)'
                type='number'
                fullWidth
                value={form.maxUsers}
                onChange={handleChange('maxUsers')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Max Rooms (optional)'
                type='number'
                fullWidth
                value={form.maxRooms}
                onChange={handleChange('maxRooms')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Description'
                fullWidth
                value={form.description}
                onChange={handleChange('description')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label='Plan Module'
                fullWidth
                value={form.propertType}
                onChange={handleChange('propertType')}
                helperText='Which company type sees this plan'>
                <MenuItem value='H'>Hotel + Restaurant</MenuItem>
                <MenuItem value='R'>Restaurant only</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isTrialPlan}
                    onChange={handleChange('isTrialPlan')}
                  />
                }
                label='Free Trial Plan'
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={handleChange('isActive')}
                  />
                }
                label='Active'
              />
            </Grid>
          </Grid>

          {/* ── Menus included in this plan ── */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>
                Menus in this plan ({form.menuKeys.length}/{MENU_LIST.length})
              </Typography>
              <Button size='small' onClick={selectAllMenus}>Select All</Button>
              <Button size='small' color='inherit' onClick={clearMenus}>Clear</Button>
            </Box>
            {form.isTrialPlan && (
              <Typography variant='caption' color='text.secondary'>
                Free Trial plans grant ALL menus automatically — this selection is ignored for trials.
              </Typography>
            )}
            <Box
              sx={{
                maxHeight: 240,
                overflowY: 'auto',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 1,
                mt: 1,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              }}
            >
              {MENU_LIST.map((m) => (
                <FormControlLabel
                  key={m.key}
                  control={
                    <Checkbox
                      size='small'
                      checked={form.menuKeys.includes(m.key)}
                      onChange={() => toggleMenu(m.key)}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>{m.label}</span>}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant='contained' onClick={savePlan} disabled={loading}>
              {form.planId > 0 ? 'Update Plan' : 'Save Plan'}
            </Button>
            {form.planId > 0 && (
              <Button variant='outlined' onClick={resetForm}>
                Cancel
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ── Plans table ── */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Max Users</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Max Rooms</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align='center' sx={{ py: 4 }}>
                  No plans yet. Add your first plan above.
                </TableCell>
              </TableRow>
            )}
            {plans.map((p) => (
              <TableRow key={p.planId} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{p.planName}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {p.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  {currency}
                  {p.price}
                </TableCell>
                <TableCell>{p.durationDays} days</TableCell>
                <TableCell>{p.maxUsers ?? '—'}</TableCell>
                <TableCell>{p.maxRooms ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={p.propertType === 'R' ? 'Resto' : 'Hotel + Resto'}
                    size='small'
                    variant='outlined'
                    color={p.propertType === 'R' ? 'warning' : 'primary'}
                  />
                </TableCell>
                <TableCell>
                  {p.isTrialPlan ? (
                    <Chip label='Trial' color='info' size='small' />
                  ) : (
                    <Chip label='Paid' size='small' />
                  )}
                </TableCell>
                <TableCell>
                  {p.isActive ? (
                    <Chip label='Active' color='success' size='small' />
                  ) : (
                    <Chip label='Inactive' color='default' size='small' />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size='small' onClick={() => editPlan(p)}>
                      Edit
                    </Button>
                    <Switch
                      checked={p.isActive}
                      onChange={() => toggleActive(p)}
                      size='small'
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
}
